/** Rulonные: навигация «Тип → Серия → Полотно» как у раздвижных (facets). */

export type RulonSibling = {
  group_id: string;
  name: string;
  subtype_tags?: string[];
  min_price?: number;
  variants_count?: number;
};

export type RulonSelection = {
  installType: string | null;
  series: string | null;
  fabric: string | null;
};

export const RULON_INSTALL_TYPES = [
  {
    id: 'Открытая',
    label: 'Открытая',
    hint: 'Классический рулон: ткань на открытом валу, компактно и универсально для жилых комнат.',
  },
  {
    id: 'В коробе',
    label: 'В коробе',
    hint: 'Алюминиевый короб скрывает механизм и защищает ткань от выгорания — аккуратный вид у окна.',
  },
  {
    id: 'Габаритный',
    label: 'Габаритный',
    hint: 'Для крупных проёмов: усиленный профиль и привод под большие ширины.',
  },
  {
    id: 'UNI-3',
    label: 'UNI-3',
    hint: 'Система UNI-3: компактный корпус и готовые решения под стандартные проёмы.',
  },
] as const;

export type RulonNavOptionCopy = { id: string; label: string; hint: string };

export function resolveRulonNavOptions<T extends RulonNavOptionCopy>(
  defaults: readonly T[],
  overrides?: RulonNavOptionCopy[] | null,
): T[] {
  if (!overrides?.length) return [...defaults];
  const byId = new Map(overrides.map((o) => [o.id, o]));
  return defaults.map((d) => {
    const o = byId.get(d.id);
    if (!o) return { ...d };
    return {
      ...d,
      label: o.label?.trim() || d.label,
      hint: o.hint?.trim() || d.hint,
    };
  });
}

export const RULON_SERIES = [
  { id: '38 мм', label: '38 мм', hint: 'Узкий профиль — для небольших и средних окон.' },
  { id: '50 мм', label: '50 мм', hint: 'Универсальный диаметр для большинства помещений.' },
  { id: '65 мм', label: '65 мм', hint: 'Усиленный вал — для широких и тяжёлых полотен.' },
] as const;

export const RULON_FABRIC_LINES = [
  {
    id: 'Классическая',
    label: 'Классическая',
    hint: 'Одно полотно на валу — стандартное затемнение и простое управление.',
  },
  {
    id: 'День-ночь',
    label: 'День-ночь',
    hint: 'Чередующиеся полосы: регулируете свет без полного закрытия окна.',
  },
] as const;

function tagSet(tags: string[] | undefined): Set<string> {
  return new Set((tags ?? []).filter(Boolean));
}

function hasTag(tags: Set<string>, section: string, label: string): boolean {
  return tags.has(`${section}::${label}`);
}

/** Fallback when API ещё без subtype_tags. */
export function inferRulonTagsFromGroupId(groupId: string): string[] {
  const slug = groupId.includes('::') ? groupId.split('::')[1]?.toLowerCase() ?? '' : '';
  const tags: string[] = [];
  const add = (section: string, label: string) => {
    const v = `${section}::${label}`;
    if (!tags.includes(v)) tags.push(v);
  };
  if (slug === 'den-noch-38mm') {
    add('Тип', 'Открытая');
    add('Серия', '38 мм');
    add('Полотно', 'День-ночь');
    return tags;
  }
  if (slug.startsWith('otkrytaya-')) {
    add('Тип', 'Открытая');
    if (slug.includes('38')) {
      add('Серия', '38 мм');
      add('Полотно', 'Классическая');
    } else if (slug.includes('50')) add('Серия', '50 мм');
    else if (slug.includes('65')) add('Серия', '65 мм');
    return tags;
  }
  if (slug.startsWith('v-korobe-')) {
    add('Тип', 'В коробе');
    if (slug.includes('38')) add('Серия', '38 мм');
    else if (slug.includes('50')) add('Серия', '50 мм');
    return tags;
  }
  if (slug === 'gabaritniy') {
    add('Тип', 'Габаритный');
    return tags;
  }
  if (slug === 'uni-3') {
    add('Тип', 'UNI-3');
    return tags;
  }
  return tags;
}

export function tagsForSibling(sg: RulonSibling): string[] {
  const fromApi = sg.subtype_tags?.filter(Boolean);
  if (fromApi?.length) return fromApi;
  return inferRulonTagsFromGroupId(sg.group_id);
}

export function selectionFromGroupId(groupId: string, siblings: RulonSibling[]): RulonSelection {
  const sg = siblings.find((s) => s.group_id === groupId);
  const tags = tagSet(sg ? tagsForSibling(sg) : inferRulonTagsFromGroupId(groupId));
  let installType: string | null = null;
  let series: string | null = null;
  let fabric: string | null = null;
  for (const t of tags) {
    const [sec, label] = t.split('::');
    if (sec === 'Тип') installType = label;
    if (sec === 'Серия') series = label;
    if (sec === 'Полотно') fabric = label;
  }
  if (installType === 'Открытая' && series === '38 мм' && !fabric) fabric = 'Классическая';
  return { installType, series, fabric };
}

export function findRulonGroupId(
  siblings: RulonSibling[],
  sel: RulonSelection,
): string | null {
  const { installType, series, fabric } = sel;
  if (!installType) return null;

  const match = siblings.find((sg) => {
    const tags = tagSet(tagsForSibling(sg));
    if (!hasTag(tags, 'Тип', installType)) return false;
    if (series && !hasTag(tags, 'Серия', series)) return false;
    if (fabric) {
      if (!hasTag(tags, 'Полотно', fabric)) return false;
    } else if (installType === 'Открытая' && series === '38 мм') {
      if (hasTag(tags, 'Полотно', 'День-ночь')) return false;
    }
    return true;
  });
  return match?.group_id ?? null;
}

export function seriesOptionsForType(
  installType: string | null,
): Array<(typeof RULON_SERIES)[number]> {
  if (!installType || installType === 'Габаритный' || installType === 'UNI-3') return [];
  if (installType === 'В коробе') return RULON_SERIES.filter((s) => s.id === '38 мм' || s.id === '50 мм');
  return [...RULON_SERIES];
}

export function showFabricStep(sel: RulonSelection): boolean {
  return sel.installType === 'Открытая' && sel.series === '38 мм';
}
